import type { ReceiptPackaging } from '@/lib/receipt-packaging';

import { api } from '../client';

import type { ListResponse, PaginationParams, SuccessResponse } from './common';
import {
  rememberResponseItemsUpdatedAt,
  rememberResponseUpdatedAt,
  type StockDocumentMutationDeps,
  type StockDocumentMutationOptions,
} from './documentMutationCache';

export type ReceiptStatus = 'draft' | 'confirmed';

/** Receipt header */
export interface Receipt {
  id: string;
  document_id?: string | null;
  location_id: string;
  supplier_id: string;
  status: ReceiptStatus;
  received_at: string;
  confirmed_at?: string | null;
  notes?: string | null;
  /** Items count for list view (when provided by backend). */
  items_count?: number | null;
  /** Quantity totals by unit code for list view. */
  unit_totals?: Record<string, string> | null;
  /** Total amount for receipt items (Decimal as string, when provided by backend). */
  total_amount?: string | null;
  has_discrepancy?: boolean | null;
  has_open_discrepancy?: boolean | null;
  items: ReceiptItem[];
  created_at?: string | null;
  created_by?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
}

/** Receipt item */
export interface ReceiptItem {
  id: string;
  receipt_id: string;
  product_id: string;
  quantity: string;
  unit_code: string;
  unit_price: string;
  vat_rate?: string | null;
  total_price: string;
  supplier_product_name?: string | null;
  document_quantity?: string | null;
  document_unit_price?: string | null;
  document_line_amount?: string | null;
  actual_quantity?: string | null;
  resolution_mode?: string | null;
  discrepancy_status?: string | null;
  accepted_stock_value?: string | null;
  resolution_reason?: string | null;
  source_kind?: 'manual' | 'document' | null;
  packaging?: ReceiptPackaging | null;
  source_document_line_index?: number | null;
  has_document_truth?: boolean;
  has_discrepancy?: boolean;
  is_confirm_ready?: boolean;
  quantity_delta?: string | null;
  created_at?: string | null;
}

/** Receipt with items */
export interface ReceiptDetail extends Receipt {
  items: ReceiptItem[];
}

export interface ReceiptDetailResponse {
  request_id: string;
  receipt: ReceiptDetail;
  already_confirmed?: boolean;
  already_cancelled?: boolean;
}

export interface ReceiptLastPurchasePrice {
  product_id: string;
  receipt_id: string;
  unit_code: string;
  unit_price: string;
  received_at: string;
}

export interface ReceiptLastPurchasePricesResponse {
  request_id: string;
  items: ReceiptLastPurchasePrice[];
}

/** Receipt filters */
export interface ReceiptFilters extends PaginationParams {
  [key: string]: string | number | boolean | undefined;
  document_id?: string;
  location_id?: string;
  supplier_id?: string;
  search?: string;
  status?: string;
  date_from?: string;
  date_to?: string;
  sort?: string;
  include_summary?: boolean;
}

/** Receipts response */
export interface ReceiptListSummary {
  by_status: Record<string, number>;
}

export interface ReceiptsResponse extends ListResponse<Receipt> {
  summary?: ReceiptListSummary | null;
}

/** Create receipt request */
export interface CreateReceiptRequest {
  allow_produced_products?: boolean;
  location_id: string;
  supplier_id: string;
  received_at: string;
  document_id?: string;
  notes?: string;
  items?: ReceiptItemRequest[];
}

/** Create receipt item */
export interface ReceiptItemRequest {
  product_id: string;
  quantity: string;
  unit_code: string;
  unit_price: string;
  vat_rate?: string;
  total_price: string;
  supplier_product_name?: string;
  document_quantity?: string;
  document_unit_price?: string;
  document_line_amount?: string;
  actual_quantity?: string;
  resolution_mode?: string;
  discrepancy_status?: string;
  accepted_stock_value?: string;
  resolution_reason?: string;
  source_kind?: 'manual' | 'document';
  source_document_line_index?: number;
}

/** Update receipt request */
export interface UpdateReceiptRequest {
  allow_produced_products?: boolean;
  location_id?: string;
  supplier_id?: string;
  received_at?: string;
  notes?: string;
  items?: ReceiptItemRequest[];
}

export interface LinkReceiptDocumentRequest {
  document_id: string;
}

export interface CancelReceiptRequest {
  cancel_reason?: string;
}

export interface MatchProductCandidate {
  product_id: string;
  product_name: string;
  confidence: number;
  source: 'mapping' | 'fuzzy';
  times_used?: number | null;
}

export interface MatchProductResult {
  supplier_product_name: string;
  candidates: MatchProductCandidate[];
}

export interface MatchProductsResponse {
  request_id: string;
  items: MatchProductResult[];
}

type ReceiptStockDocumentEntity = 'receipt';

export type StockReceiptsApiDeps = StockDocumentMutationDeps<ReceiptStockDocumentEntity>;

export const createStockReceiptsApi = ({
  rememberStockDocumentUpdatedAt,
  forgetStockDocumentUpdatedAt,
  withExpectedUpdatedAt,
}: StockReceiptsApiDeps) => ({
  list: async (filters: ReceiptFilters = {}): Promise<ReceiptsResponse> => {
    return rememberResponseItemsUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'receipt',
      await api.webAdmin.get<ReceiptsResponse>('/stock/receipts', { params: filters }),
      (response) => response.items,
      (item) => item.id,
      (item) => item.updated_at,
    );
  },

  detail: async (id: string): Promise<ReceiptDetailResponse> => {
    return rememberResponseUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'receipt',
      await api.webAdmin.get<ReceiptDetailResponse>(`/stock/receipts/${id}`),
      (response) => response.receipt.id,
      (response) => response.receipt.updated_at,
    );
  },

  create: async (
    data: CreateReceiptRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<ReceiptDetailResponse> => {
    return rememberResponseUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'receipt',
      await api.webAdmin.post<ReceiptDetailResponse>('/stock/receipts', data, options),
      (response) => response.receipt.id,
      (response) => response.receipt.updated_at,
    );
  },

  update: (
    id: string,
    data: UpdateReceiptRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<ReceiptDetailResponse> =>
    api.webAdmin
      .put<ReceiptDetailResponse>(
        `/stock/receipts/${id}`,
        data,
        withExpectedUpdatedAt('receipt', id, options),
      )
      .then((response) =>
        rememberResponseUpdatedAt(
          rememberStockDocumentUpdatedAt,
          'receipt',
          response,
          (current) => current.receipt.id,
          (current) => current.receipt.updated_at,
        ),
      ),

  delete: async (id: string, options?: StockDocumentMutationOptions): Promise<SuccessResponse> => {
    const response = await api.webAdmin.delete<SuccessResponse>(
      `/stock/receipts/${id}`,
      withExpectedUpdatedAt('receipt', id, options),
    );
    forgetStockDocumentUpdatedAt('receipt', id);
    return response;
  },

  confirm: (id: string, options?: StockDocumentMutationOptions): Promise<ReceiptDetailResponse> =>
    api.webAdmin
      .post<ReceiptDetailResponse>(
        `/stock/receipts/${id}/confirm`,
        undefined,
        withExpectedUpdatedAt('receipt', id, options),
      )
      .then((response) =>
        rememberResponseUpdatedAt(
          rememberStockDocumentUpdatedAt,
          'receipt',
          response,
          (current) => current.receipt.id,
          (current) => current.receipt.updated_at,
        ),
      ),

  cancel: (
    id: string,
    data: CancelReceiptRequest = {},
    options?: StockDocumentMutationOptions,
  ): Promise<ReceiptDetailResponse> =>
    api.webAdmin
      .post<ReceiptDetailResponse>(
        `/stock/receipts/${id}/cancel`,
        data,
        withExpectedUpdatedAt('receipt', id, options),
      )
      .then((response) =>
        rememberResponseUpdatedAt(
          rememberStockDocumentUpdatedAt,
          'receipt',
          response,
          (current) => current.receipt.id,
          (current) => current.receipt.updated_at,
        ),
      ),

  linkDocument: (
    id: string,
    data: LinkReceiptDocumentRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<ReceiptDetailResponse> =>
    api.webAdmin
      .put<ReceiptDetailResponse>(
        `/stock/receipts/${id}/document`,
        data,
        withExpectedUpdatedAt('receipt', id, options),
      )
      .then((response) =>
        rememberResponseUpdatedAt(
          rememberStockDocumentUpdatedAt,
          'receipt',
          response,
          (current) => current.receipt.id,
          (current) => current.receipt.updated_at,
        ),
      ),

  unlinkDocument: (
    id: string,
    options?: StockDocumentMutationOptions,
  ): Promise<ReceiptDetailResponse> =>
    api.webAdmin
      .delete<ReceiptDetailResponse>(
        `/stock/receipts/${id}/document`,
        withExpectedUpdatedAt('receipt', id, options),
      )
      .then((response) =>
        rememberResponseUpdatedAt(
          rememberStockDocumentUpdatedAt,
          'receipt',
          response,
          (current) => current.receipt.id,
          (current) => current.receipt.updated_at,
        ),
      ),

  lastPurchasePrices: (params: {
    supplier_id: string;
    product_ids: string[];
  }): Promise<ReceiptLastPurchasePricesResponse> =>
    api.webAdmin.get('/stock/receipts/last-purchase-prices', {
      params: {
        supplier_id: params.supplier_id,
        product_id: params.product_ids,
      },
    }),

  matchProducts: (data: {
    supplier_id: string;
    items: { supplier_product_name: string }[];
    limit?: number;
  }): Promise<MatchProductsResponse> => api.webAdmin.post('/stock/receipts/match-products', data),
});
